/**
 * Main ↔ AudioWorklet music-scheduler protocol.
 * Clip/transport commands go in; playhead + mirrored note events come out.
 * Kernel DSP messages remain bare KernelMessage on the same port.
 */
import type { ClipIR } from "../clip/types";
import type { PlayheadInfo } from "../clip/patternPlayer";
import type { KernelMessage } from "../types";

export type WorkletMusicIn =
  | { type: "music"; action: "config"; lookahead: number }
  | {
      type: "music";
      action: "play";
      clip: ClipIR;
      startStep?: number;
    }
  | { type: "music"; action: "stop" }
  | { type: "music"; action: "setClip"; clip: ClipIR }
  | {
      type: "music";
      action: "setMuteSolo";
      muted?: boolean[];
      solo?: boolean[];
    }
  | { type: "music"; action: "setOpenHoldDefaultSlots"; flags: boolean[] };

export type WorkletMusicOut =
  | { type: "musicPlayhead"; info: PlayheadInfo | undefined }
  | { type: "musicEnded" }
  /** Mirror of scheduled note traffic for External MIDI (best-effort). */
  | { type: "musicNote"; message: KernelMessage };

export function isWorkletMusicIn(value: unknown): value is WorkletMusicIn {
  return (
    typeof value === "object"
    && value !== null
    && (value as { type?: string }).type === "music"
    && typeof (value as { action?: string }).action === "string"
  );
}

export function isWorkletMusicOut(value: unknown): value is WorkletMusicOut {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const t = (value as { type?: string }).type;
  return t === "musicPlayhead" || t === "musicEnded" || t === "musicNote";
}
